// Trace levels
export type TraceLevel = 'off' | 'basic' | 'verbose';

// Phase taxonomy (extend as needed)
export type Phase =
  // high-level
  | 'discover_mount' | 'deck_loaded' | 'create_click' | 'generate_start' | 'generate_ok' | 'generate_err'
  | 'nav_to_plans' | 'plans_loaded'
  // verbose events
  | 'swipe_like' | 'swipe_skip'
  | 'deck_fetch_start' | 'deck_fetch_ok' | 'deck_fetch_err'
  | 'plans_query_start' | 'plans_query_ok' | 'plans_query_err'
  | 'usage_query_start' | 'usage_query_ok' | 'usage_query_err';

type Payload = Record<string, unknown>;

// Do not read env in this build. Control tracing via:
//   window.__TRACE_LEVEL = 'off' | 'basic' | 'verbose'
// or localStorage.setItem('TRACE_LEVEL', 'verbose') in the browser.
const RAW_LEVEL = (
  (typeof window !== 'undefined' && (window as any).__TRACE_LEVEL) ||
  (typeof localStorage !== 'undefined' && localStorage.getItem('TRACE_LEVEL')) ||
  'off'
) as string | undefined;

const LEVEL: TraceLevel = (['off','basic','verbose'] as const).includes(RAW_LEVEL as any)
  ? (RAW_LEVEL as TraceLevel)
  : 'off';

const PREFIX = 'TRACE:';           // grep anchor
const START = Date.now();

// Redact keys that should never leak (extendable)
const SENSITIVE_KEYS = new Set([
  'email','token','apiKey','authorization','auth','secret','stripeKey','openaiKey','password'
]);

function sanitize(payload: Payload): Payload {
  const out: Payload = {};
  for (const [k, v] of Object.entries(payload || {})) {
    if (SENSITIVE_KEYS.has(k)) out[k] = '[redacted]';
    else out[k] = v;
  }
  return out;
}

// Decide if an event is allowed at current level
function allowed(phase: Phase): boolean {
  if (LEVEL === 'off') return false;
  if (LEVEL === 'basic') {
    return [
      'discover_mount','deck_loaded','create_click','generate_start','generate_ok','generate_err',
      'nav_to_plans','plans_loaded'
    ].includes(phase);
  }
  // verbose
  return true;
}

/** Emit a single-line JSON trace. Never logs secrets. Zero work if disabled. */
export function trace(phase: Phase, payload: Payload = {}): void {
  if (!allowed(phase)) return;
  // eslint-disable-next-line no-console
  console.log(
    PREFIX + JSON.stringify({ t: Date.now(), dt: Date.now() - START, phase, ...sanitize(payload) })
  );
}
